import { api } from './api';
import { Articolo } from '../types';

/**
 * Interface for creating or updating an Articolo
 */
export interface ArticoloRequest {
  codice?: string;
  nome: string;
  descrizione?: string;
  note?: string;
  prezzoAcquisto?: number;
  prezzoVendita?: number;
  unitaMisura?: string;
  categoriaId?: number;
  categoriaArticoloId?: number;
  attivo?: boolean;
  quantita?: number;
  scortaMinima?: number;
}

/**
 * Interface for filtering Articoli
 */
export interface ArticoloFilters {
  nome?: string;
  categoriaArticoloId?: number;
}

/**
 * Get all articoli with optional filters
 */
const getArticoli = async (filters?: ArticoloFilters): Promise<Articolo[]> => {
  const params = new URLSearchParams();

  if (filters?.nome) {
    params.append('nome', filters.nome);
  }

  if (filters?.categoriaArticoloId !== undefined) {
    params.append('categoriaArticoloId', filters.categoriaArticoloId.toString());
  }

  const queryString = params.toString();
  const url = queryString ? `/articoli?${queryString}` : '/articoli';

  const response = await api.get<Articolo[]>(url);
  return response.data;
};

/**
 * Get a single articolo by ID
 */
const getArticoloById = async (id: number): Promise<Articolo> => {
  const response = await api.get<Articolo>(`/articoli/${id}`);
  return response.data;
};

/**
 * Create a new articolo
 */
const createArticolo = async (articolo: ArticoloRequest): Promise<Articolo> => {
  const response = await api.post<Articolo>('/articoli', articolo);
  return response.data;
};

/**
 * Update an existing articolo
 */
const updateArticolo = async (id: number, articolo: ArticoloRequest): Promise<Articolo> => {
  const response = await api.put<Articolo>(`/articoli/${id}`, articolo);
  return response.data;
};

/**
 * Delete an articolo
 */
const deleteArticolo = async (id: number): Promise<void> => {
  await api.delete(`/articoli/${id}`);
};

/**
 * Export Articoli to CSV
 */
const exportArticoli = async (): Promise<void> => {
  const response = await api.get('/articoli/export', {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'];
  let filename = 'articoli.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Get movements for a specific articolo
 */
interface MovimentoSummary {
  id: number;
  descrizione?: string;
  importo: number;
  tipo: string;
  data: string;
  categoriaNome?: string;
  numeroOrdine?: string;
  quantita?: number;
}

const getMovimentiByArticolo = async (articoloId: number): Promise<MovimentoSummary[]> => {
  const response = await api.get<MovimentoSummary[]>(`/articoli/${articoloId}/movimenti`);
  return response.data;
};

const articoliService = {
  getArticoli,
  getArticoloById,
  createArticolo,
  updateArticolo,
  deleteArticolo,
  exportArticoli,
  getMovimentiByArticolo,
};

export type { MovimentoSummary };
export default articoliService;
